/**
 * ConfirmPlanningModal
 * Modal for confirming that an assignment is ready for execution.
 * Notifies assigned TGs via SMS with assignment details.
 */

import { useState } from 'react'
import { X, CheckCircle, Calendar, User, Leaf, AlertTriangle, Loader2, MessageSquare, ChevronDown, ChevronRight, MapPin, Clock } from 'lucide-react'

interface TgInfo {
  id: string
  subcontractorId: string
  name: string
  phone: string | null
}

export interface PlanningConfirmationResult {
  sendSmsTo: Record<string, boolean>  // keyed by assignment_subcontractor id
  extraInfo: Record<string, string>   // keyed by assignment_subcontractor id
}

interface ConfirmPlanningModalProps {
  isOpen: boolean
  onClose: () => void
  assignmentNumber?: string
  assignmentTitle: string
  assignmentDate: string
  assignmentAddress?: string
  startTime?: string | null
  customerName: string
  customerConfirmed: boolean
  tgList: TgInfo[]
  warnings?: string[]
  onConfirm: (result: PlanningConfirmationResult) => Promise<void>
}

export function ConfirmPlanningModal({
  isOpen,
  onClose,
  assignmentNumber,
  assignmentTitle,
  assignmentDate,
  assignmentAddress,
  startTime,
  customerName,
  customerConfirmed,
  tgList,
  warnings = [],
  onConfirm
}: ConfirmPlanningModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [sendSmsTo, setSendSmsTo] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {}
    tgList.forEach(tg => {
      // Default to true if TG has phone number
      initial[tg.id] = !!tg.phone
    })
    return initial
  })
  const [extraInfo, setExtraInfo] = useState<Record<string, string>>({})
  const [expandedSms, setExpandedSms] = useState<Record<string, boolean>>({})

  const toggleSendSms = (tgId: string) => {
    setSendSmsTo(prev => ({ ...prev, [tgId]: !prev[tgId] }))
  }

  const toggleSmsExpanded = (tgId: string) => {
    setExpandedSms(prev => ({ ...prev, [tgId]: !prev[tgId] }))
  }

  const updateExtraInfo = (tgId: string, text: string) => {
    setExtraInfo(prev => ({ ...prev, [tgId]: text }))
  }

  const handleConfirm = async () => {
    setIsSubmitting(true)
    try {
      await onConfirm({
        sendSmsTo,
        extraInfo
      })
      onClose()
    } catch (error) {
      console.error('Error confirming planning:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!isOpen) return null

  const allRequirementsMet = tgList.length > 0 && assignmentDate

  // Format time to HH:MM (remove seconds if present)
  const formatTime = (time: string | null | undefined) => {
    if (!time) return ''
    return time.substring(0, 5)
  }

  // Format assignment identifier
  const getAssignmentIdentifier = () => {
    if (assignmentNumber) {
      return `#${assignmentNumber} ${assignmentTitle}`
    }
    return assignmentTitle
  }

  // Format date with time
  const formatDateTime = () => {
    const formattedTime = formatTime(startTime)
    if (formattedTime) {
      return `${assignmentDate} kl ${formattedTime}`
    }
    return assignmentDate
  }

  // Get SMS message for TG
  const getSmsMessage = (tgId: string) => {
    const identifier = getAssignmentIdentifier()
    const dateTime = formatDateTime()
    const addressPart = assignmentAddress ? `${assignmentAddress}, ` : ''
    const extra = extraInfo[tgId]?.trim()

    let message = `Du är tilldelad uppdraget ${identifier}. ${addressPart}${dateTime}.`
    if (extra) {
      message += ` ${extra}`
    }
    return message
  }

  // Count total SMS to be sent
  const getTotalSmsCount = () => {
    return tgList.filter(tg => tg.phone && sendSmsTo[tg.id]).length
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-nature-200 dark:border-nature-700 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-nature-900 dark:text-nature-100">
                  Bekräfta planering
                </h3>
                <p className="text-sm text-nature-500 dark:text-nature-400">
                  Markera uppdraget som planerat och meddela TG:s
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-nature-400 hover:text-nature-600 dark:hover:text-nature-300 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content - scrollable */}
        <div className="px-6 py-4 space-y-5 overflow-y-auto flex-1">
          {/* Assignment summary */}
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
            <h4 className="font-medium text-nature-900 dark:text-nature-100 mb-3">
              {getAssignmentIdentifier()}
            </h4>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2 text-nature-600 dark:text-nature-400">
                <Calendar className="h-4 w-4" />
                <span>{assignmentDate || 'Inget datum'}</span>
                {!assignmentDate && <span className="text-red-500">*</span>}
              </div>
              {startTime && (
                <div className="flex items-center gap-2 text-nature-600 dark:text-nature-400">
                  <Clock className="h-4 w-4" />
                  <span>{formatTime(startTime)}</span>
                </div>
              )}
              {assignmentAddress && (
                <div className="flex items-center gap-2 text-nature-600 dark:text-nature-400">
                  <MapPin className="h-4 w-4" />
                  <span>{assignmentAddress}</span>
                </div>
              )}
              <div className="flex items-center gap-2 text-nature-600 dark:text-nature-400">
                <User className="h-4 w-4" />
                <span>{customerName}</span>
              </div>
            </div>
          </div>

          {/* Requirements check */}
          <div className="space-y-2">
            <p className="text-sm font-medium text-nature-700 dark:text-nature-300">
              Planeringskrav:
            </p>
            <div className="space-y-1">
              <div className={`flex items-center gap-2 text-sm ${assignmentDate ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                {assignmentDate ? (
                  <CheckCircle className="h-4 w-4" />
                ) : (
                  <AlertTriangle className="h-4 w-4" />
                )}
                <span>Datum satt</span>
              </div>
              <div className={`flex items-center gap-2 text-sm ${tgList.length > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                {tgList.length > 0 ? (
                  <CheckCircle className="h-4 w-4" />
                ) : (
                  <AlertTriangle className="h-4 w-4" />
                )}
                <span>Trädgårdsmästare tilldelad</span>
              </div>
              <div className={`flex items-center gap-2 text-sm ${customerConfirmed ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'}`}>
                {customerConfirmed ? (
                  <CheckCircle className="h-4 w-4" />
                ) : (
                  <AlertTriangle className="h-4 w-4" />
                )}
                <span>Kund bekräftad {!customerConfirmed && '(valfritt)'}</span>
              </div>
            </div>
          </div>

          {/* Warnings */}
          {warnings.length > 0 && (
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-amber-700 dark:text-amber-300">Varningar:</p>
                  <ul className="mt-1 text-sm text-amber-600 dark:text-amber-400 list-disc list-inside">
                    {warnings.map((warning, index) => (
                      <li key={index}>{warning}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* TG notification section */}
          {tgList.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-nature-700 dark:text-nature-300">
                <Leaf className="h-4 w-4" />
                <span className="font-medium">Meddela trädgårdsmästare</span>
                <span className="text-sm text-nature-500">({tgList.length} tilldelade)</span>
              </div>

              <div className="space-y-3 pl-6">
                {tgList.map(tg => {
                  const canSendSms = !!tg.phone
                  const willSendSms = canSendSms && sendSmsTo[tg.id]
                  const isExpanded = expandedSms[tg.id]

                  return (
                    <div key={tg.id} className="border border-nature-200 dark:border-nature-700 rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-nature-800 dark:text-nature-200">{tg.name}</span>
                        </div>
                        {/* SMS indicator */}
                        {canSendSms && (
                          <button
                            type="button"
                            onClick={() => toggleSmsExpanded(tg.id)}
                            className={`flex items-center gap-1 text-xs px-2 py-1 rounded transition-colors ${
                              willSendSms
                                ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                                : 'bg-nature-100 text-nature-500 dark:bg-nature-700 dark:text-nature-400'
                            }`}
                          >
                            <MessageSquare className="h-3 w-3" />
                            <span>{willSendSms ? 'SMS' : 'Inget SMS'}</span>
                            {isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                          </button>
                        )}
                        {!tg.phone && (
                          <span className="text-xs text-nature-400 dark:text-nature-500 italic">
                            Inget telefonnummer
                          </span>
                        )}
                      </div>

                      {/* SMS Preview - collapsible */}
                      {canSendSms && isExpanded && (
                        <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-medium text-blue-700 dark:text-blue-300">
                              SMS till {tg.phone}
                            </span>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={sendSmsTo[tg.id] || false}
                                onChange={() => toggleSendSms(tg.id)}
                                className="h-3.5 w-3.5 text-primary-600 rounded"
                              />
                              <span className="text-xs text-blue-600 dark:text-blue-400">Skicka</span>
                            </label>
                          </div>

                          {/* Extra info input */}
                          <div className="mb-2">
                            <input
                              type="text"
                              value={extraInfo[tg.id] || ''}
                              onChange={(e) => updateExtraInfo(tg.id, e.target.value)}
                              placeholder="Extra info (valfritt)..."
                              className="w-full px-2 py-1 text-xs border border-blue-200 dark:border-blue-700 rounded bg-white dark:bg-nature-800 text-nature-900 dark:text-nature-100"
                            />
                          </div>

                          <div className={`bg-white dark:bg-nature-800 rounded p-2 border border-blue-200 dark:border-blue-700 ${!sendSmsTo[tg.id] ? 'opacity-50' : ''}`}>
                            <p className="text-xs text-nature-700 dark:text-nature-300 font-mono">
                              {getSmsMessage(tg.id)}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {tgList.length === 0 && (
            <div className="text-center py-4 text-nature-500 dark:text-nature-400 text-sm italic">
              Inga trädgårdsmästare tilldelade
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-nature-200 dark:border-nature-700 bg-nature-50 dark:bg-nature-900 flex-shrink-0">
          <div className="flex items-center justify-between">
            {/* SMS summary */}
            <div className="text-sm text-nature-500 dark:text-nature-400">
              {getTotalSmsCount() > 0 ? (
                <span className="flex items-center gap-1">
                  <MessageSquare className="h-4 w-4 text-blue-500" />
                  {getTotalSmsCount()} SMS skickas
                </span>
              ) : (
                <span className="italic">Inga SMS skickas</span>
              )}
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={onClose}
                className="btn-outline px-4 py-2"
                disabled={isSubmitting}
              >
                Avbryt
              </button>
              <button
                onClick={handleConfirm}
                disabled={isSubmitting || !allRequirementsMet}
                className="btn-primary px-4 py-2 flex items-center space-x-2 disabled:opacity-50"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Sparar...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4" />
                    <span>Bekräfta planering</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
